import { useCallback, useEffect, useRef, useState } from 'react';
import { clientFor, useAccounts } from '../auth/store';
import type { Mailbox as JmapMailbox } from '../api/types';

/*
    The account's folders, kept live.

    This lives outside the mailbox view because the sidebar does: Settings renders inside the same
    shell, and the folder list has to be there and correct on it too. It is also fetched exactly
    once — two callers would mean two of everything below.
*/

/** Lets the poller nudge whatever message list is currently mounted. */
export const bumpRef: { current: (() => void) | null } = { current: null };

/*
    Dropping a message onto a folder.

    The folder tree lives in the shell and the optimistic move lives in the mail
    screen, so the screen publishes its handler here rather than the shell
    reimplementing the move without the list state to update.
*/
export const dropRef: { current: ((emailId: string, mailboxId: string) => void) | null } = { current: null };

export function useMailboxes(accountKey: string | undefined) {
    const status = useAccounts((s) => s.status);
    const accounts = useAccounts((s) => s.accounts);
    const [mailboxes, setMailboxes] = useState<JmapMailbox[]>([]);
    const [error, setError] = useState<string | null>(null);
    // Held in a ref so the folder tree can ask for a refresh after it changes
    // something, without the effect re-running and re-subscribing to push.
    const reloadRef = useRef<() => void>(() => {});
    // The current list, for the poller to ask about without re-running the effect each time it
    // changes — which would restart the timer on every refresh.
    const mailboxesRef = useRef<JmapMailbox[]>([]);
    const account = accounts.find((a) => a.id === accountKey);
    // Not gated on a ready flag: the client connects on demand, so folders load
    // on the first paint after a refresh rather than after a round trip.
    const ready = accountKey ? status[accountKey] !== 'error' : false;

    useEffect(() => {
        setMailboxes([]);
        if (!account || !ready) return;
        const client = clientFor(account.id);
        if (!client) return;
        let cancelled = false;

        const load = async () => {
            try {
                const list = await client.mailboxes(account.accountId);
                if (!cancelled) {
                    setMailboxes(list);
                    mailboxesRef.current = list;
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : String(e));
            }
        };

        reloadRef.current = () => void load();
        void load();

        /*
            Noticing new mail.

            Iris Mail was told: its server pushed over a websocket, and anything that changed
            anywhere appeared at once. Nothing here can do that. IMAP's version is IDLE, which
            holds a connection open for as long as it waits — and a session has two, so one tab
            watching its inbox would take half of them and a second tab the rest.

            So it asks instead, with a STATUS on the folders it is showing: one short round trip,
            no mailbox opened, nothing disturbed. Every thirty seconds while the window has focus,
            and immediately on regaining it, because that is the moment somebody is looking.

            The check is cheap enough to be frequent and the full folder list is only refetched
            when the check says something actually moved — UIDNEXT going up means mail arrived,
            and the unread count changing means somebody read something somewhere else.
        */
        let signature = '';

        const check = async () => {
            if (document.visibilityState === 'hidden') return;
            try {
                const names = mailboxesRef.current.filter((m) => m.selectable).map((m) => m.id);
                if (!names.length) {
                    void load();
                    return;
                }
                const status = await client.status(names.slice(0, 50));
                const next = status.map((f) => `${f.folder}:${f.uidNext}:${f.unseen}`).join('|');
                if (signature && next !== signature) {
                    void load();
                    bumpRef.current?.();
                }
                signature = next;
            } catch {
                /* a failed check is not worth reporting; the next one is 30 seconds away */
            }
        };

        const timer = setInterval(() => void check(), 30_000);
        const onFocus = () => void check();
        window.addEventListener('focus', onFocus);

        return () => {
            cancelled = true;
            clearInterval(timer);
            window.removeEventListener('focus', onFocus);
        };
    }, [account, ready]);

    const reload = useCallback(() => reloadRef.current(), []);
    return { mailboxes, error, reload };
}
